import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, GitCompare, History, PenLine, Trash2, Plus, Copy, Send, ArrowLeftRight, Pencil, Check, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { TYPE_LABEL, TYPE_STYLE, STATUS_STYLE, fmtMoney } from "./ContractList";
import DocDiffDialog from "@/components/DocDiffDialog";
import { apiErrorMessage } from "@/lib/apiError";

export default function ContractDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("items"); // items | audit | versions
  const [showCompare, setShowCompare] = useState(false);
  const [compareLeft, setCompareLeft] = useState("");
  const [compareRight, setCompareRight] = useState("");
  const [compareResult, setCompareResult] = useState(null);
  const [showSigDialog, setShowSigDialog] = useState(false);
  const [diffing, setDiffing] = useState(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: vs }] = await Promise.all([
        api.get(`/contracts/${cid}`),
        api.get(`/contracts/${cid}/versions`),
      ]);
      setContract(c);
      setVersions(vs || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "audit") api.get(`/contracts/${cid}/audit`).then(({ data }) => setAudit(data || []));
  }, [tab, cid]);

  const saveHeader = async () => {
    try {
      await api.patch(`/contracts/${cid}`, headerDraft);
      setEditingHeader(false); setHeaderDraft({});
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const restoreVersion = async () => {
    await api.post(`/contracts/${cid}/versions/${diffing.id}/restore`);
    load();
  };
  const openDiff = async (v) => {
    try {
      const { data: full } = await api.get(`/contracts/${cid}/versions/${v.id}`);
      setDiffing(full);
    } catch { toast.error("Failed to load version"); }
  };

  const runCompare = async () => {
    if (!compareLeft || !compareRight) { toast.error("Pick two versions"); return; }
    try {
      const { data } = await api.get(`/contracts/${cid}/compare`, {
        params: { left: compareLeft, right: compareRight },
      });
      setCompareResult(data);
    } catch { toast.error("Failed to compare"); }
  };

  const addLine = async () => {
    const name = window.prompt("Line item name?");
    if (!name) return;
    const rate = Number(window.prompt("Rate?", "0")) || 0;
    const qty = Number(window.prompt("Qty?", "1")) || 1;
    try {
      await api.post(`/contracts/${cid}/line-items`, { name, rate, qty, unit: "unit" });
      load();
    } catch { toast.error("Failed"); }
  };

  const delLine = async (lid) => {
    try { await api.delete(`/contract-line-items/${lid}`); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!contract) return <div className="p-6 text-sm text-muted-foreground">Contract not found</div>;

  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="contract-detail-page">
      <div className="flex items-center gap-2 text-xs">
        <Link to="/app/contracts" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> All Contracts
        </Link>
        {contract.parent_contract && (
          <Link to={`/app/contracts/${contract.parent_contract.id}`} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Parent: {contract.parent_contract.number}
          </Link>
        )}
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-mono text-muted-foreground">{contract.number}</span>
            <span className={`inline-block px-2 py-0.5 border text-[10px] ${TYPE_STYLE[contract.contract_type]}`}>{TYPE_LABEL[contract.contract_type]}</span>
            <span className={`inline-block px-2 py-0.5 border text-[10px] ${STATUS_STYLE[contract.status] || STATUS_STYLE.draft}`}>{contract.status}</span>
            <span className="text-[10px] text-muted-foreground">v{contract.version || 1}</span>
            {["sent", "approved", "signed"].includes(contract.status) && (
              <span className="text-[10px] px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-700" title="Edits still allowed — a snapshot is captured on save">
                edits versioned
              </span>
            )}
          </div>
          {editingHeader ? (
            <div className="mt-2 space-y-2 max-w-xl">
              <input
                value={headerDraft.title ?? contract.title}
                onChange={(e) => setHeaderDraft({ ...headerDraft, title: e.target.value })}
                className="w-full text-xl font-bold px-2 py-1 bg-background border border-border"
                data-testid="contract-edit-title"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Start date</label>
                  <input
                    type="date"
                    value={(headerDraft.start_date ?? contract.start_date ?? "").slice(0, 10)}
                    onChange={(e) => setHeaderDraft({ ...headerDraft, start_date: e.target.value || null })}
                    className="w-full text-xs px-2 py-1 bg-background border border-border"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">End date</label>
                  <input
                    type="date"
                    value={(headerDraft.end_date ?? contract.end_date ?? "").slice(0, 10)}
                    onChange={(e) => setHeaderDraft({ ...headerDraft, end_date: e.target.value || null })}
                    className="w-full text-xs px-2 py-1 bg-background border border-border"
                  />
                </div>
              </div>
              <textarea
                value={headerDraft.notes ?? (contract.notes || "")}
                onChange={(e) => setHeaderDraft({ ...headerDraft, notes: e.target.value })}
                placeholder="Notes"
                rows={2}
                className="w-full text-xs px-2 py-1 bg-background border border-border"
                data-testid="contract-edit-notes"
              />
              <div className="flex gap-2">
                <button type="button" onClick={saveHeader} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1" data-testid="contract-edit-save">
                  <Check className="w-3 h-3" /> Save
                </button>
                <button type="button" onClick={() => { setEditingHeader(false); setHeaderDraft({}); }} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1">
                  <X className="w-3 h-3" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <h1 className="text-2xl font-bold">{contract.title}</h1>
              <button type="button" onClick={() => setEditingHeader(true)} className="p-1 text-muted-foreground hover:text-foreground" title="Edit" data-testid="contract-edit-btn">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1.5">
            {contract.client_name && <>Client: <b>{contract.client_name}</b>{contract.brand_name && <span> · Brand: <b>{contract.brand_name}</b></span>}</>}
            {contract.start_date && <span className="ml-2">· {contract.start_date}{contract.end_date ? ` → ${contract.end_date}` : ""}</span>}
            {contract.last_editor_name && <span className="ml-2">· Last edit by <b>{contract.last_editor_name}</b></span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {versions.length >= 2 && (
            <button type="button" onClick={() => setShowCompare(true)} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="contract-compare-btn">
              <GitCompare className="w-3.5 h-3.5" /> Compare
            </button>
          )}
          {["draft", "sent", "approved"].includes(contract.status) && (
            <button type="button" onClick={() => setShowSigDialog(true)} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="contract-esign-btn">
              <PenLine className="w-3.5 h-3.5" /> Send for e-sign
            </button>
          )}
        </div>
      </div>

      {/* Parent + Children */}
      {(contract.parent_contract || (contract.children && contract.children.length > 0)) && (        <div className="border border-border p-3 text-xs bg-muted/20 space-y-1">
          {contract.parent_contract && (
            <div>Parent: <Link to={`/app/contracts/${contract.parent_contract.id}`} className="text-primary hover:underline">{contract.parent_contract.number} · {contract.parent_contract.title}</Link></div>
          )}
          {contract.children && contract.children.length > 0 && (
            <div>Children: {contract.children.map((k) => (
              <Link key={k.id} to={`/app/contracts/${k.id}`} className="text-primary hover:underline mr-2">
                {k.number} ({TYPE_LABEL[k.contract_type]})
              </Link>
            ))}</div>
          )}
        </div>
      )}

      {/* Recurring / renewal */}
      <RecurringPanel contract={contract} onSaved={load} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {[
          { k: "items", label: "Line items" },
          { k: "audit", label: `Audit trail${audit.length ? ` (${audit.length})` : ""}` },
          { k: "versions", label: `Versions${versions.length ? ` (${versions.length})` : ""}` },
        ].map((t) => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)} className={`text-xs px-3 py-2 border-b-2 -mb-px ${tab === t.k ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`contract-tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "items" && (
      <div className="border border-border">
        <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide">Line items ({contract.line_items?.length || 0})</div>
          <button type="button" onClick={addLine} className="text-xs px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1" data-testid="contract-add-line-btn">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-muted/20 border-b border-border">
            <tr className="text-left text-[10px] uppercase text-muted-foreground">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2 w-16">Qty</th>
              <th className="px-3 py-2 w-16">Unit</th>
              <th className="px-3 py-2 w-24">Rate</th>
              {contract.contract_type === "sow" && <th className="px-3 py-2 w-28">Delivery</th>}
              <th className="px-3 py-2 w-16">Tax %</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(contract.line_items || []).map((li) => (
              <tr key={li.id} data-testid={`contract-line-${li.id}`}>
                <td className="px-3 py-2 font-medium">{li.name}</td>
                <td className="px-3 py-2">{li.qty}</td>
                <td className="px-3 py-2">{li.unit}</td>
                <td className="px-3 py-2 font-mono">{fmtMoney(li.rate, contract.currency)}</td>
                {contract.contract_type === "sow" && <td className="px-3 py-2 text-muted-foreground">{li.delivery_date || "—"}</td>}
                <td className="px-3 py-2">{li.tax_pct || 0}%</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => delLine(li.id)} className="p-1 text-muted-foreground hover:text-rose-500">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
            {(contract.line_items || []).length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No line items.</td></tr>
            )}
          </tbody>
        </table>
        {(contract.grand_total || 0) > 0 && (
          <div className="border-t border-border bg-muted/20 p-3 text-xs flex justify-end gap-4">
            <span className="text-muted-foreground">Subtotal: <span className="font-mono">{fmtMoney(contract.subtotal, contract.currency)}</span></span>
            <span className="text-muted-foreground">Tax: <span className="font-mono">{fmtMoney(contract.tax_amount, contract.currency)}</span></span>
            <span className="font-bold">Total: <span className="font-mono" data-testid="contract-grand-total">{fmtMoney(contract.grand_total, contract.currency)}</span></span>
          </div>
        )}
      </div>
      )}

      {tab === "audit" && (
        <ContractAuditTrail rows={audit} />
      )}

      {tab === "versions" && (
        <div className="border border-border">
          <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center gap-2">
            <History className="w-3.5 h-3.5" />
            <div className="text-xs font-semibold uppercase tracking-wide">Version history ({versions.length})</div>
          </div>
          <div className="divide-y divide-border">
            {versions.length === 0 && (
              <div className="p-8 text-center text-xs text-muted-foreground">No prior versions yet.</div>
            )}
            {versions.map((v) => (
              <div key={v.id} className="px-4 py-2 text-xs flex items-center justify-between hover:bg-muted/30 cursor-pointer" onClick={() => setDiffing(v)} data-testid={`contract-version-${v.id}`}>
                <div>v{v.version} · <span className="text-muted-foreground">{v.line_items?.length || 0} lines</span> · <span className="text-muted-foreground">{new Date(v.created_at).toLocaleString()}</span></div>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={(e) => { e.stopPropagation(); setDiffing(v); }} className="text-primary hover:underline text-[11px] inline-flex items-center gap-1" data-testid={`contract-version-diff-${v.id}`}>
                    <ArrowLeftRight className="w-3 h-3" /> Compare with current
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setCompareRight(v.id); setShowCompare(true); }} className="text-[10px] text-muted-foreground hover:text-foreground">Compare with another…</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compare dialog */}
      {showCompare && (
        <CompareDialog
          versions={versions}
          left={compareLeft} right={compareRight}
          setLeft={setCompareLeft} setRight={setCompareRight}
          onRun={runCompare} result={compareResult}
          onClose={() => { setShowCompare(false); setCompareResult(null); }}
        />
      )}

      {showSigDialog && (
        <SignatureRequestDialog
          wsId={activeWorkspaceId}
          docType={contract.contract_type}
          docId={contract.id}
          onClose={() => setShowSigDialog(false)}
          onSent={() => { setShowSigDialog(false); load(); toast.success("Signature request sent"); }}
        />
      )}

      {diffing && contract && (
        <DocDiffDialog
          version={diffing}
          current={contract}
          currentLines={contract.line_items || []}
          fields={[
            { key: "title", label: "Title" },
            { key: "status", label: "Status" },
            { key: "start_date", label: "Start date", kind: "date" },
            { key: "end_date", label: "End date", kind: "date" },
            { key: "grand_total", label: "Grand total", kind: "money" },
            { key: "notes", label: "Notes" },
            { key: "is_recurring", label: "Recurring" },
          ]}
          lineFields={[
            { key: "name", label: "Name" },
            { key: "qty", label: "Qty" },
            { key: "unit", label: "Unit" },
            { key: "rate", label: "Rate", kind: "money" },
            { key: "tax_pct", label: "Tax %", kind: "pct" },
            { key: "delivery_date", label: "Delivery", kind: "date" },
          ]}
          onRestore={restoreVersion}
          onClose={() => setDiffing(null)}
          title={`Contract ${contract.number} · Version diff`}
        />
      )}
    </div>
  );
}

function ContractAuditTrail({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="contract-audit-empty">No audit events yet.</div>;
  }
  return (
    <div className="border border-border divide-y divide-border" data-testid="contract-audit-trail">
      {rows.map((r) => (
        <div key={r.id} className="p-3 text-xs" data-testid={`contract-audit-row-${r.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 text-[10px] border ${r.action?.includes("post_send") || r.action === "restore_from_version" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-700 border-slate-300"}`}>{r.action}</span>
              <span className="font-medium">{r.actor_name || r.actor_id}</span>
            </div>
            <span className="text-muted-foreground">{fmtRelative(r.at)}</span>
          </div>
          {r.changes && Object.keys(r.changes).length > 0 && (
            <div className="mt-2 pl-2 border-l-2 border-border space-y-0.5">
              {Object.entries(r.changes).map(([k, v]) => (
                <div key={k} className="text-[11px]">
                  <span className="font-mono text-muted-foreground">{k}</span>
                  {v && typeof v === "object" && "from" in v ? (
                    <span> · <span className="line-through text-rose-600">{String(v.from ?? "—")}</span> → <span className="text-emerald-700">{String(v.to ?? "—")}</span></span>
                  ) : (
                    <span>: {typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {r.version_snapshot_id && (
            <div className="mt-1 text-[10px] text-muted-foreground">snapshot: <span className="font-mono">{r.version_snapshot_id.slice(0, 8)}</span></div>
          )}
        </div>
      ))}
    </div>
  );
}

function CompareDialog({ versions, left, right, setLeft, setRight, onRun, result, onClose }) {
  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="contract-compare-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl bg-background border border-border shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm">Compare Versions</div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            <select value={left} onChange={(e) => setLeft(e.target.value)} className="text-xs px-2 py-1.5 bg-background border border-border" data-testid="compare-left">
              <option value="">Select left version</option>
              {versions.map((v) => <option key={v.id} value={v.id}>v{v.version} · {new Date(v.created_at).toLocaleString()}</option>)}
            </select>
            <select value={right} onChange={(e) => setRight(e.target.value)} className="text-xs px-2 py-1.5 bg-background border border-border" data-testid="compare-right">
              <option value="">Select right version</option>
              {versions.map((v) => <option key={v.id} value={v.id}>v{v.version} · {new Date(v.created_at).toLocaleString()}</option>)}
            </select>
          </div>
          <button type="button" onClick={onRun} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground" data-testid="compare-run">Compare</button>

          {result && (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Changed fields</div>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {result.changed_fields.length === 0 ? <span className="text-[10px] text-muted-foreground">No top-level changes</span> :
                   result.changed_fields.map((f) => <span key={f} className="px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 text-[10px]">{f}</span>)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[11px]">
                <div className="border border-border p-2 bg-rose-50/40">
                  <div className="text-[10px] font-semibold text-rose-700 mb-1">Left · v{result.left.version}</div>
                  <div className="font-medium">{result.left.snapshot?.title}</div>
                  <div className="mt-2 text-muted-foreground">Lines: {result.left.line_items?.length || 0}</div>
                </div>
                <div className="border border-border p-2 bg-emerald-50/40">
                  <div className="text-[10px] font-semibold text-emerald-700 mb-1">Right · v{result.right.version}</div>
                  <div className="font-medium">{result.right.snapshot?.title}</div>
                  <div className="mt-2 text-muted-foreground">Lines: {result.right.line_items?.length || 0}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Line-item diff</div>
                <div className="text-[11px] mt-1 space-y-1">
                  <div>Added: {result.line_diff.added.length} · Removed: {result.line_diff.removed.length} · Changed: {result.line_diff.changed.length}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Close</button>
        </div>
      </div>
    </div>
  );
}

function SignatureRequestDialog({ wsId, docType, docId, onClose, onSent }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [channels, setChannels] = useState({ email: true, whatsapp: false });
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email required"); return; }
    setSending(true);
    try {
      const chList = [];
      if (channels.email) chList.push("email");
      if (channels.whatsapp) chList.push("whatsapp");
      await api.post(`/workspaces/${wsId}/signature-requests`, {
        doc_type: docType, doc_id: docId,
        signer_name: name.trim(), signer_email: email.trim(),
        signer_phone: phone.trim() || undefined,
        channels: chList,
        message: message.trim() || undefined,
      });
      onSent();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sig-request-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm">Send for e-signature</div>
        <div className="p-4 space-y-3 text-xs">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Signer name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="sig-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Signer email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="sig-email" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Phone (for WhatsApp)</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91…" className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Deliver via</label>
            <div className="flex gap-3 mt-1 text-xs">
              <label className="flex items-center gap-1"><input type="checkbox" checked={channels.email} onChange={(e) => setChannels({ ...channels, email: e.target.checked })} data-testid="sig-channel-email" /> Email</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={channels.whatsapp} onChange={(e) => setChannels({ ...channels, whatsapp: e.target.checked })} data-testid="sig-channel-wa" /> WhatsApp</label>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Custom message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={send} disabled={sending} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium flex items-center gap-1" data-testid="sig-send">
            <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecurringPanel({ contract, onSaved }) {
  const [isRec, setIsRec] = useState(!!contract.is_recurring);
  const [days, setDays] = useState(contract.renewal_days_before || 30);
  const [months, setMonths] = useState(contract.recurrence_period_months || 12);
  const [saving, setSaving] = useState(false);
  const dirty = isRec !== !!contract.is_recurring
    || Number(days) !== (contract.renewal_days_before || 30)
    || Number(months) !== (contract.recurrence_period_months || 12);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/contracts/${contract.id}`, {
        is_recurring: isRec,
        renewal_days_before: Number(days) || 30,
        recurrence_period_months: Number(months) || 12,
      });
      toast.success("Renewal settings saved");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border bg-background" data-testid="contract-recurring-panel">
      <div className="px-4 py-2 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-wide">Recurring / auto-renewal</div>
      <div className="p-4 space-y-3 text-xs">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isRec} onChange={(e) => setIsRec(e.target.checked)} data-testid="contract-recurring-toggle" />
          <span>This is a recurring retainer contract</span>
        </label>
        {isRec && (
          <div className="grid grid-cols-2 gap-3 pl-6">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Remind me N days before end date</label>
              <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="contract-recurring-days" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Recurrence period (months)</label>
              <input type="number" min="1" value={months} onChange={(e) => setMonths(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="contract-recurring-months" />
            </div>
          </div>
        )}
        {contract.renewal_reminded_at && (
          <div className="text-[10px] text-emerald-700 pl-6">
            Last renewal reminder sent {new Date(contract.renewal_reminded_at).toLocaleDateString()} ({contract.renewal_reminded_days_left} days before end)
          </div>
        )}
        <div className="flex justify-end">
          <button type="button" disabled={!dirty || saving} onClick={save} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-40 font-medium" data-testid="contract-recurring-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
